// A real ghost for the instance pools: alpha-blended, like Model edits.
//
// The "disabled" bucket used to be a gray tint through instanceColor and
// nothing else: on a dark material the tint is barely a shade off, so a
// revision review that "ghosts everything unchanged" left the unchanged
// model looking like the model, and the change did not read. What a
// reviewer expects is the see-through shell the Model edits drawer draws
// for the previous state.
//
// Per-instance transparency cannot live in one InstancedMesh: every
// instance shares one draw call, so the opaque instances would be sorted
// and blended with the ghosted ones. So each pool draws TWO meshes over
// the same instance buffers (matrices, colours, and a per-instance
// instanceGhost flag on the geometry):
//
//   - the solid mesh, whose shader discards ghosted instances, and
//   - a ghost twin with a transparent clone of the material that
//     discards everything BUT the ghosted instances, pulls what is left
//     toward light gray, multiplies alpha by GHOST_OPACITY and writes no
//     depth. It is only visible while the pool holds a ghosted slot.
//
// Patched once per material through onBeforeCompile; the pool gives every
// mesh the attribute so no program ever references an attribute a
// geometry lacks.
import { markSceneChanged } from './renderSignal';
import { singlePassBlend } from './blendPass';
export const GHOST_OPACITY = 0.18;

// The ghost for items that share ONE draw with solid ones - a tile's
// BatchedMesh and its merged singleton mesh (tileBatch.js, tileMerge.js).
// They cannot blend per item, so a ghosted item is drawn as a screen-door
// HALFTONE: light grey, one pixel in four, no depth-sorting needed. Before
// this they only took the 0.45 grey tint and printed as solid dark shapes
// (owner, 2026-09-18: "halftone for every element" in the tracking export).
// Their per-item state texture flags a ghost with alpha 2 (1 = drawn,
// 0 = hidden), carried to the fragment in `varName`.
export const SLOT_GHOST_ALPHA = 2;
export const slotHalftoneFrag = (varName) => `
if ( ${varName} > 0.5 ) {
  if ( mod( floor( gl_FragCoord.x ), 2.0 ) + mod( floor( gl_FragCoord.y ), 2.0 ) > 0.5 ) discard;
  diffuseColor.rgb = mix( diffuseColor.rgb, vec3( 0.84, 0.87, 0.90 ), 0.85 );
}
`;

const VERT_DECL = 'attribute float instanceGhost;\nvarying float vGhost;\n';
const VERT_ASSIGN = 'vGhost = instanceGhost;\n';
const FRAG_DECL = 'uniform float uGhostOpacity;\nvarying float vGhost;\n';
const FRAG_SOLID = '\nif (vGhost > 0.5) discard;\n';
const FRAG_GHOST = `
if (vGhost < 0.5) discard;
diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.84, 0.87, 0.90), 0.85);
diffuseColor.a *= uGhostOpacity;
`;

function patchShader(material, mode) {
  material.userData = material.userData || {};
  if (material.userData.__ghostMode === mode) return material;
  material.userData.__ghostMode = mode;
  const previous = material.onBeforeCompile;
  material.onBeforeCompile = (shader, renderer) => {
    if (typeof previous === 'function') previous(shader, renderer);
    shader.uniforms.uGhostOpacity = { value: GHOST_OPACITY };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\n' + VERT_DECL)
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n' + VERT_ASSIGN);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\n' + FRAG_DECL)
      .replace('#include <color_fragment>', '#include <color_fragment>\n' + (mode === 'ghost' ? FRAG_GHOST : FRAG_SOLID));
  };
  // The patched program differs from the stock one for the same material
  // type, and the two modes from each other; keep them apart in the
  // program cache.
  const prevKey = typeof material.customProgramCacheKey === 'function'
    ? material.customProgramCacheKey.bind(material) : null;
  material.customProgramCacheKey = () => (prevKey ? prevKey() : '') + '|ghost:' + mode;
  material.needsUpdate = true;
  return material;
}

// The pool's solid material: draws everything that is not ghosted.
export function ensureGhostShader(material) {
  if (!material) return material;
  return patchShader(material, 'solid');
}

// The transparent twin of a pool material: draws only what is ghosted.
// One clone per source material, cached on it, so pools sharing a
// material share the twin too.
export function ghostTwinMaterial(material) {
  if (!material) return material;
  material.userData = material.userData || {};
  if (material.userData.__ghostTwin) return material.userData.__ghostTwin;
  const twin = material.clone();
  // clone() copies userData, including the solid patch's marker and the
  // cached twin pointer - neither belongs on the twin.
  twin.userData = {};
  twin.transparent = true;
  twin.opacity = 1;          // alpha is applied per fragment in the shader
  twin.depthWrite = false;   // a shell never occludes what sits behind it
  singlePassBlend(twin);     // one draw for a double-sided twin (blendPass.js)
  twin.name = (material.name || '') + ' (ghost)';
  patchShader(twin, 'ghost');
  material.userData.__ghostTwin = twin;
  return twin;
}

// Write one slot's ghost flag, keep the pool's ghost count on the
// attribute, and show or hide the twin accordingly. Cheap enough to call
// per slot: nothing is marked for re-upload unless the value changes.
export function writeGhostAt(mesh, index, ghosted) {
  // A tile batch or merged tile mesh keeps its per-item state in a
  // texture and draws a ghosted item as a halftone (SLOT_HALFTONE_FRAG).
  if (mesh && typeof mesh.setGhostAt === 'function') { mesh.setGhostAt(index, !!ghosted); return; }
  const geometry = mesh && mesh.geometry;
  const attr = geometry && geometry.getAttribute && geometry.getAttribute('instanceGhost');
  if (!attr || index < 0 || index >= attr.array.length) return;
  const v = ghosted ? 1 : 0;
  if (attr.array[index] === v) return;
  attr.array[index] = v;
  attr.needsUpdate = true;
  markSceneChanged('ghost');
  attr.ghostCount = Math.max(0, (attr.ghostCount || 0) + (ghosted ? 1 : -1));
  syncGhostTwin(mesh);
}

// The twin is drawn only while the pool is visible AND holds a ghosted
// slot: a second draw per pool costs vertex work, so it is not paid for
// pools with nothing to ghost.
export function syncGhostTwin(mesh) {
  const twin = mesh && mesh.__ghostTwin;
  if (!twin) return;
  const attr = mesh.geometry && mesh.geometry.getAttribute && mesh.geometry.getAttribute('instanceGhost');
  const count = attr ? (attr.ghostCount || 0) : 0;
  twin.count = mesh.count;
  twin.visible = !!mesh.visible && count > 0;
}

// Count the ghosted slots in a freshly (re)built attribute.
export function recountGhosts(attr) {
  if (!attr || !attr.array) return 0;
  let n = 0;
  for (let i = 0; i < attr.array.length; i++) if (attr.array[i] > 0.5) n++;
  attr.ghostCount = n;
  return n;
}
